import React from 'react';
import { Button, Icon, Image, Text } from '@msc-b2c/shared-components';
import { ReservationRecapStepProps } from '@msc-b2c/interfaces/interfaces';
import { formatSlotTime } from '../../utils/diningAvailabilityUtils';
import {
  getObsPlaceholderImagePath,
  replacePlaceholders,
  RootState,
  useAssetAltText,
} from '@msc-b2c/shared-utils';
import styles from './reservationRecapStep.module.scss';
import { useSelector } from 'react-redux';

/**
 * Renders the OBS thumbnail for a selected restaurant. Extracted into its own
 * component so the alt-text hook can be called once per row (hooks cannot be
 * called inside `.map()` callbacks).
 */
const ReservationThumbnail: React.FC<{
  restaurantImage: string;
  restaurantCode: string;
  fallbackAlt: string;
}> = ({ restaurantCode, fallbackAlt, restaurantImage }) => {
  const localizedAlt = useAssetAltText(restaurantCode, 'obs');
  return (
    <Image
      src={restaurantImage}
      alt={localizedAlt || fallbackAlt}
      placeholderImagePath={getObsPlaceholderImagePath()}
      styleOption={'cmp-image__overlay'}
    />
  );
};

export const ReservationRecapStep: React.FC<ReservationRecapStepProps> = ({
  diningReservationRecapProps,
  dayLabel,
  dayAtSeaLabel,
  selectedRestaurants,
  canAddAnother,
  onEdit,
  onRemove,
  onAddAnother,
  isAlreadyPurchasedFlow,
}) => {
  const specialityRestaurantsFromCf = useSelector(
    (state: RootState) => state.myAreaCommonConfig.specialtyRestaurants
  );

  return (
    <div className={styles['reservation-recap']}>
      <div className={styles['reservation-recap__items']}>
        {selectedRestaurants.map((reservation, reservationIndex) => {
          const restaurantFromCF = specialityRestaurantsFromCf?.find((item) => {
            return item.roomCode === reservation.restaurantCode;
          });

          const dayDescriptor = reservation.isAtSea
            ? dayAtSeaLabel
            : reservation.portName ?? dayAtSeaLabel;
          const recapDesc = [
            reservation.dayNumber !== undefined
              ? `${dayLabel} ${reservation.dayNumber}, ${dayDescriptor}`
              : dayDescriptor,
            reservation.time && formatSlotTime(reservation.time),
          ]
            .filter(Boolean)
            .join(', ');
          const confirmationMessage = replacePlaceholders(
            diningReservationRecapProps?.reservationConfirmedLabel,
            { recapDesc }
          );

          return (
            <div
              key={`selected-restaurant-${reservation.restaurantCode}-${reservationIndex}`}
              className={styles['reservation-recap__item']}
            >
              <div className={styles['reservation-recap__thumbnail']}>
                <ReservationThumbnail
                  restaurantCode={reservation.restaurantCode}
                  restaurantImage={
                    restaurantFromCF?.restaurantImage?.imgDmPath ||
                    restaurantFromCF?.restaurantImage?.imgPath ||
                    ''
                  }
                  fallbackAlt={reservation.restaurantName ?? reservation.restaurantCode}
                />
                <div className={styles['reservation-recap__header']}>
                  <Text
                    text={restaurantFromCF?.restaurantName || reservation.restaurantName || ''}
                    styleOption="header-name"
                  />
                  {reservation.restaurantName && (
                    <Text
                      text={reservation.restaurantName}
                      styleOption="header-desc"
                    />
                  )}
                </div>
              </div>

              <div className={styles['reservation-recap__content']}>
                <div className={styles['reservation-recap__content__icon']}>
                  {diningReservationRecapProps?.reservationConfirmedIcon && (
                    <Icon icon={diningReservationRecapProps.reservationConfirmedIcon} />
                  )}
                </div>
                <div className={styles['reservation-recap__content__body']}>
                  <Text
                    text={confirmationMessage}
                    styleOption="confirmation-info"
                  />
                  <div className={styles['reservation-recap__content__actions']}>
                    <Button
                      text={diningReservationRecapProps?.editLabel}
                      styleOption={'cmp-button__link'}
                      onClick={() => {
                        onEdit(reservationIndex);
                      }}
                    />
                    {!isAlreadyPurchasedFlow && (
                      <Button
                        text={diningReservationRecapProps?.removeLabel}
                        styleOption={'cmp-button__link'}
                        onClick={() => {
                          onRemove(reservationIndex);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {canAddAnother && onAddAnother && (
        <div className={styles['reservation-recap__add-another']}>
          <Button
            text={diningReservationRecapProps?.addAnotherLabel}
            onClick={onAddAnother}
            styleOption={'reservation-recap__add-another-button cmp-button__primary'}
          />
        </div>
      )}
    </div>
  );
};

export default ReservationRecapStep;
